<template>
  <v-card :data-cy="`organ-${field.id}`" outlined class="pa-4 rounded-lg h-100">
    <h4 class="mb-3">{{ field.name }}</h4>
    <v-row dense>
      <v-col cols="12" sm="9">
        <h4 class="mb-1">
          {{ $t("inputs.captations.captured")
          }}<span style="color: red">*</span>
        </h4>
        <v-radio-group
          v-model="field.orgon_captured_status_id"
          :disabled="field.disabled"
          @change="$emit('block-selection-changed', field)"
          row
          :rules="formRules"
        >
          <v-radio color="green" :label="$t('options.yes')" value="captado" />
          <v-radio
            color="green"
            :label="$t('options.yes_in_block')"
            value="captado-em-bloco"
            v-if="canBeInBlock"
          />
          <v-radio
            color="green"
            :label="$t('options.not')"
            value="nao-captado"
          />
        </v-radio-group>
      </v-col>

      <v-col
        cols="12"
        sm="3"
        class="d-flex justify-end"
        v-if="showCaptationDate"
      >
        <v-text-field
          data-cy="hora-captacao"
          class="rounded-lg"
          color="#086318"
          type="datetime-local"
          outlined
          dense
          :max="maxDateForOrgonsCaptation"
          :min="minDateForOrgonsCaptation"
          :rules="dateRules"
          v-model="field.captation_hour"
        >
          <template v-slot:label>
            {{ $t("inputs.captations.captation_hour")
            }}<span style="color: red">*</span>
          </template>
        </v-text-field>
      </v-col>
    </v-row>

    <v-row dense v-if="showCaptationDate">
      <v-col cols="12" sm="2">
        <v-select
          data-cy="orgon-biopsy"
          class="rounded-lg"
          color="#086318"
          v-model="field.biopsy"
          :items="yesorNot"
          :rules="isRim ? formRules : []"
          item-text="name"
          item-value="value"
          dense
          outlined
        >
          <template v-slot:label>
            {{ $t("inputs.captations.biopsy") }}
            <span style="color: red">{{ isRim ? "*" : "" }}</span>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="3">
        <v-select
          data-cy="acondicionado"
          class="rounded-lg"
          color="#086318"
          v-model="field.packed"
          :items="yesorNot"
          :rules="formRules"
          item-text="name"
          item-value="value"
          dense
          outlined
        >
          <template v-slot:label>
            {{ $t("inputs.captations.packed") }}
            <span style="color: red">*</span>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="2">
        <v-select
          data-cy="solucao-preservacao"
          class="rounded-lg"
          color="#086318"
          v-model="field.preservation_solutions_id"
          :items="solucoes"
          :rules="formRules"
          item-text="name"
          item-value="id"
          dense
          outlined
        >
          <template v-slot:label>
            {{ $t("inputs.captations.preservation_solutions") }}
            <span style="color: red">*</span>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="2">
        <v-text-field
          class="rounded-lg"
          color="#086318"
          outlined
          dense
          v-model="field.number_lot"
          :label="$t('inputs.captations.number_lot')"
        />
      </v-col>

      <v-col cols="12" sm="3">
        <v-text-field
          class="rounded-lg"
          color="#086318"
          outlined
          dense
          type="date"
          :max="maxDateToLoteValidation"
          v-model="field.lot_expiration_date"
          :label="$t('inputs.captations.lot_expiration_date')"
        />
      </v-col>
    </v-row>

    <v-row dense v-if="showNephrectomy">
      <v-col cols="12">
        <v-card outlined class="pa-4 rounded-lg h-100">
          <h4 class="mb-3">
            <v-icon small color="#41916b">mdi-hospital</v-icon
            >{{ $t("inputs.captations.nephrectomy") }} {{ field.name }}:
          </h4>
          <v-row>
            <v-col cols="12" sm="2">
              <v-radio-group
                :rules="formRules"
                v-model="field.normal_morphology"
              >
                <p>
                  {{ $t("inputs.captations.normal_morphology")
                  }}<span style="color: red">*</span>
                </p>
                <v-radio
                  :label="$t('options.yes')"
                  value="SIM"
                  color="#086318"
                />
                <v-radio
                  :label="$t('options.not')"
                  value="NÃO"
                  color="#086318"
                />
              </v-radio-group>
            </v-col>
            <v-col cols="12" sm="3">
              <v-radio-group
                :rules="formRules"
                v-model="field.single_arterial_pedicle"
              >
                <p>
                  {{ $t("inputs.captations.single_arterial_pedicle") }} <br />{{
                    $t("inputs.captations.unic")
                  }}<span style="color: red">*</span>
                </p>
                <v-radio
                  :label="$t('options.yes')"
                  value="SIM"
                  color="#086318"
                />
                <v-radio
                  :label="$t('options.not')"
                  value="NÃO"
                  color="#086318"
                />
              </v-radio-group>
            </v-col>
            <v-col cols="12" sm="3">
              <v-radio-group
                :rules="formRules"
                v-model="field.single_venous_pedicle"
              >
                <p>
                  {{ $t("inputs.captations.single_venous_pedicle") }} <br />{{
                    $t("inputs.captations.unic")
                  }}<span style="color: red">*</span>
                </p>
                <v-radio
                  :label="$t('options.yes')"
                  value="SIM"
                  color="#086318"
                />
                <v-radio
                  :label="$t('options.not')"
                  value="NÃO"
                  color="#086318"
                />
              </v-radio-group>
            </v-col>
            <v-col cols="12" sm="2">
              <v-radio-group
                :rules="formRules"
                v-model="field.single_ureteral_rout"
              >
                <p>
                  {{ $t("inputs.captations.single_ureteral_rout") }} Única?<span
                    style="color: red"
                    >*</span
                  >
                </p>
                <v-radio
                  :label="$t('options.yes')"
                  value="SIM"
                  color="#086318"
                />
                <v-radio
                  :label="$t('options.not')"
                  value="NÃO"
                  color="#086318"
                />
              </v-radio-group>
            </v-col>
            <v-col cols="12" sm="2">
              <v-radio-group
                :rules="formRules"
                v-model="field.adequate_perfusion"
              >
                <p>
                  {{ $t("inputs.captations.adequate_perfusion")
                  }}<span style="color: red">*</span>
                </p>
                <v-radio
                  :label="$t('options.yes')"
                  value="SIM"
                  color="#086318"
                />
                <v-radio
                  :label="$t('options.not')"
                  value="NÃO"
                  color="#086318"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense v-if="showCorneaFields">
      <v-col class="d-flex mt-3" cols="12">
        <v-select
          data-cy="descricao-cirurgica"
          class="rounded-lg"
          color="#086318"
          v-model="field.surgical_description"
          :items="surgicalDescriptions"
          :rules="formRules"
          item-text="description"
          item-value="id"
          chips
          multiple
          outlined
        >
          <template v-slot:label>
            {{ $t("inputs.captations.surgical_description") }}
            <span style="color: red">*</span>
          </template>
        </v-select>
      </v-col>
      <v-col
        class="d-flex"
        cols="12"
        :sm="field.intercorrency == '1' ? '6' : '12'"
      >
        <v-select
          data-cy="intercorrencia"
          class="rounded-lg"
          color="#086318"
          v-model="field.intercorrency"
          :items="yesorNot"
          :rules="formRules"
          item-text="name"
          item-value="value"
          dense
          outlined
        >
          <template v-slot:label>
            {{ $t("inputs.captations.intercorrency") }}
            <span style="color: red">*</span>
          </template>
        </v-select>
      </v-col>
      <v-col class="d-flex" cols="12" sm="6" v-if="field.intercorrency == '1'">
        <v-text-field
          class="rounded-lg"
          color="#086318"
          v-model="field.description"
          :rules="formRules"
          dense
          outlined
        >
          <template v-slot:label>
            {{ $t("inputs.captations.intercorrency_description") }}
            <span style="color: red">*</span>
          </template>
        </v-text-field>
      </v-col>
    </v-row>

    <v-row dense v-if="field.orgon_captured_status_id == 'nao-captado'">
      <v-col cols="12">
        <v-select
          data-cy="motivo-nao-captacao"
          class="rounded-lg"
          color="#086318"
          v-model="field.reason_not_captured_id"
          :rules="formRules"
          :items="notCapturedReasons"
          item-text="description"
          item-value="id"
          outlined
          dense
        >
          <template v-slot:label>
            {{ $t("inputs.captations.reason_not_captured_orgon") }}
            <span style="color: red">*</span>
          </template>
        </v-select>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { ORGONS_IN_BLOCK } from "./constants/captacaoConstants";

export default {
  props: {
    field: { type: Object, required: true },
    solucoes: { type: Array, default: () => [] },
    yesorNot: { type: Array, default: () => [] },
    formRules: { type: Array, default: () => [] },
    dateRules: { type: Array, default: () => [] },
    maxDateForOrgonsCaptation: { type: String, default: null },
    minDateForOrgonsCaptation: { type: String, default: null },
    maxDateToLoteValidation: { type: String, default: null },
    surgicalDescriptions: { type: Array, default: () => [] },
    reasonsForNotCapturedOrgons: { type: Array, default: () => [] },
    reasonsForNotCapturedTissues: { type: Array, default: () => [] },
  },

  computed: {
    isRim() {
      const slug =
        (this.field.orgon && this.field.orgon.slug) || this.field.slug;
      return slug === "rim-esquerdo" || slug === "rim-direito";
    },
    isTissues() {
      const typeOrgonPath = this.field.type_orgon
        ? this.field.type_orgon.description
        : this.field.orgon.type_orgon.description;
      return typeOrgonPath === "Tecido";
    },
    canBeInBlock() {
      const slug =
        (this.field.orgon && this.field.orgon.slug) || this.field.slug;

      return ORGONS_IN_BLOCK.includes(slug);
    },
    showCaptationDate() {
      return (
        ["captado", "captado-em-bloco"].includes(
          this.field.orgon_captured_status_id,
        ) && !this.field.disabled
      );
    },
    showNephrectomy() {
      return (
        ["captado", "captado-em-bloco"].includes(
          this.field.orgon_captured_status_id,
        ) && this.isRim
      );
    },
    showCorneaFields() {
      return this.field.orgon_captured_status_id == "captado" && this.isTissues;
    },
    notCapturedReasons() {
      return this.isTissues
        ? this.reasonsForNotCapturedTissues
        : this.reasonsForNotCapturedOrgons;
    },
  },
};
</script>
